<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import type { IToastProps, IToastEmits } from "./VToast.types";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VInlineButton from "@magnit/core/src/components/VInlineButton/VInlineButton.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";

const SERVICE_DURATION = 5;
const INFO_DURATION = 3;
const props = withDefaults(defineProps<IToastProps>(), {
  theme: "success",
  type: "info",
  position: "top",
  active: true,
  gap: "8px",
});
const emit = defineEmits<IToastEmits>();

const isActive = ref(true);
const timer = ref<ReturnType<typeof setTimeout>>();
const duration = computed(() => {
  if (props.persistent) return 0;
  return props.theme === "info" ? INFO_DURATION : SERVICE_DURATION;
});

const themeIconName = computed(() => {
  switch (props.theme) {
    case "error":
      return "24-system-error";
    case "info":
      return "24-outline-info";
    case "warning":
      return "24-system-warning";
    case "question":
      return "24-system-question";
    case "success":
    default:
      return "24-system-success";
  }
});
const themeIconColor = computed(() => {
  switch (props.theme) {
    case "info":
      return "invert-secondary";
    default:
      return "inherit";
  }
});

const onButtonClick = () => {
  emit("clickButton");
  onClose();
};
const onClose = () => {
  isActive.value = false;
  emit("close");
};

onMounted(() => {
  if (!duration.value) return;
  timer.value = setTimeout(onClose, duration.value * 1000);
});
onUnmounted(() => {
  if (timer.value) clearTimeout(timer.value);
});
</script>

<template>
  <div class="pl-toast__outer">
    <transition
      :name="`pl-toast_fade-${position}`"
      appear
      @after-leave="$emit('destroy')"
    >
      <div v-if="isActive" class="pl-toast">
        <template v-if="!hideIcon">
          <slot>
            <VIcon :color="themeIconColor" :name="themeIconName" />
          </slot>
        </template>

        <div class="pl-toast__content">
          <VText font="body-small-accent-low" color="invert-primary">
            {{ title }}
          </VText>
          <VText
            v-if="description"
            font="caption-large-regular-low"
            color="tertiary"
          >
            {{ description }}
          </VText>
        </div>

        <VInlineButton v-if="buttonLabel" theme="invert" @click="onButtonClick">
          {{ buttonLabel }}
        </VInlineButton>
      </div>
    </transition>
  </div>
</template>

<style lang="postcss" scoped>
.pl-toast {
  display: flex;
  align-items: center;
  gap: var(--pl-unit-x3);
  padding: var(--pl-unit-x3);
  border-radius: var(--pl-toast-radius-s);
  box-shadow: var(--pl-shadow-xl);
  background-color: var(--pl-toast-background-default);
  min-width: 320px;
  max-width: 375px;

  &__outer {
    margin-bottom: v-bind(gap);
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &_fade-top-enter-active,
  &_fade-bottom-enter-active {
    transition: all 0.3s ease-out;
  }

  &_fade-top-leave-active,
  &_fade-bottom-leave-active {
    transition: all 0.6s ease-out;
  }

  &_fade-top-enter-from {
    transform: translateY(-100%);
    opacity: 0;
  }

  &_fade-bottom-enter-from {
    transform: translateY(100%);
    opacity: 0;
  }

  &_fade-top-enter-to,
  &_fade-bottom-enter-to,
  &_fade-top-leave-from,
  &_fade-bottom-leave-from {
    opacity: 1;
  }

  &_fade-top-leave-to,
  &_fade-bottom-leave-to {
    opacity: 0;
  }
}
</style>
